import React, { useEffect, useRef, useState } from "react";

/* =========================================================================
   EDITORIAL POLISH
   Chapter cover motifs, chapter meta chips, between-chapter pull-quotes,
   animated number counters, product-tinted ledger rows.
   All optional — driven by SHOW_EDITORIAL_POLISH flag in Home.jsx.
   ========================================================================= */

/* ------------------------------------------------------------------
   Chapter cover motif — small hairline SVG above the CH.NN badge
   ------------------------------------------------------------------ */

const MOTIFS = {
  argument: (
    <svg width="34" height="34" viewBox="0 0 40 40" fill="none" aria-hidden>
      <circle cx="20" cy="20" r="15" stroke="currentColor" strokeWidth="1.1" />
      <ellipse cx="20" cy="20" rx="15" ry="6" stroke="currentColor" strokeWidth="1.1" />
      <ellipse cx="20" cy="20" rx="6" ry="15" stroke="currentColor" strokeWidth="1.1" />
      <line x1="5" y1="20" x2="35" y2="20" stroke="currentColor" strokeWidth="0.6" opacity="0.4" />
    </svg>
  ),
  deny: (
    <svg width="34" height="34" viewBox="0 0 40 40" fill="none" aria-hidden>
      <circle cx="14" cy="20" r="6" stroke="currentColor" strokeWidth="1.1" />
      <circle cx="14" cy="20" r="2" fill="currentColor" />
      <line x1="20" y1="20" x2="34" y2="20" stroke="currentColor" strokeWidth="1.1" />
      <line x1="28" y1="20" x2="28" y2="26" stroke="currentColor" strokeWidth="1.1" />
      <line x1="32" y1="20" x2="32" y2="24" stroke="currentColor" strokeWidth="1.1" />
    </svg>
  ),
  stack: (
    <svg width="34" height="34" viewBox="0 0 40 40" fill="none" aria-hidden>
      <rect x="6" y="10" width="28" height="4" stroke="currentColor" strokeWidth="1.1" />
      <rect x="6" y="16" width="28" height="4" stroke="currentColor" strokeWidth="1.1" />
      <rect x="6" y="22" width="28" height="4" stroke="currentColor" strokeWidth="1.1" />
      <rect x="6" y="28" width="28" height="4" stroke="currentColor" strokeWidth="1.1" />
    </svg>
  ),
  deployments: (
    <svg width="34" height="34" viewBox="0 0 40 40" fill="none" aria-hidden>
      <circle cx="20" cy="20" r="14" stroke="currentColor" strokeWidth="1.1" />
      <polygon points="20,6 22,18 20,20 18,18" fill="currentColor" />
      <polygon points="20,34 22,22 20,20 18,22" stroke="currentColor" strokeWidth="0.8" opacity="0.6" />
      <polygon points="6,20 18,18 20,20 18,22" stroke="currentColor" strokeWidth="0.8" opacity="0.6" />
      <polygon points="34,20 22,18 20,20 22,22" stroke="currentColor" strokeWidth="0.8" opacity="0.6" />
    </svg>
  ),
};

export function ChapterCover({ motif }) {
  return <div className="e2-ch-cover" data-motif={motif}>{MOTIFS[motif] || null}</div>;
}

/* ------------------------------------------------------------------
   Chapter meta chips — 3 tiny monospace facts under each CH.NN badge
   ------------------------------------------------------------------ */

export function ChapterMeta({ items }) {
  return (
    <div className="e2-ch-meta" role="list">
      {items.map((it, i) => (
        <span className="e2-ch-meta-item" role="listitem" key={i}>{it}</span>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------
   Pull-quote between chapters — full-width italic serif break
   ------------------------------------------------------------------ */

export function PullQuote({ children, mark }) {
  return (
    <section className="e2-pullquote" data-testid="e2-pullquote">
      <div className="e2-shell e2-pullquote-inner">
        <div className="e2-pullquote-mark">{mark || "§"}</div>
        <blockquote className="e2-pullquote-body">{children}</blockquote>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------
   CountUp — animates a numeric label from 0 to target on first view.
   Preserves any trailing/leading characters ("150+", etc.).
   ------------------------------------------------------------------ */

export function CountUp({ value, duration = 1400, format = (n) => n }) {
  // Extract numeric part + suffix
  const match = String(value).match(/^([\d,\.]+)(.*)$/);
  const target = match ? Number(match[1].replace(/,/g, "")) : null;
  const suffix = match ? match[2] : "";
  const [display, setDisplay] = useState(target === null ? value : "0" + suffix);
  const ref = useRef(null);
  const played = useRef(false);

  useEffect(() => {
    if (target === null) { setDisplay(value); return; }
    const node = ref.current;
    if (!node) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !played.current) {
          played.current = true;
          const start = performance.now();
          const tick = (t) => {
            const p = Math.min(1, (t - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            const n = Math.round(target * eased);
            setDisplay(format(n) + suffix);
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.35 });
    io.observe(node);
    return () => io.disconnect();
  }, [target, suffix, duration, value, format]);

  if (target === null) return <span>{value}</span>;
  return <span ref={ref}>{display}</span>;
}

/* ------------------------------------------------------------------
   Product tint helper — returns the css class for a product name
   ------------------------------------------------------------------ */

const PRODUCT_TINT = {
  Realloc: "prod-realloc",
  Foundation: "prod-foundation",
  Cloud: "prod-cloud",
  Atom: "prod-atom",
};

export function productTint(name) {
  return PRODUCT_TINT[name] || "";
}
